/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {defineMessages} from 'react-intl'
import {
    UserIcon,
    BookIcon,
    CubeIcon,
    PrivacyIcon,
    WishlistIcon,
    RepairIcon
} from '../../components/custom-icons'

export const messages = defineMessages({
    profile: {
        defaultMessage: 'Personal Information',
        id: 'global.account.link.personal_information'
    },
    addresses: {defaultMessage: 'Address Book', id: 'global.account.link.address_book'},
    orders: {defaultMessage: 'Order History', id: 'global.account.link.order_history'},
    privacy: {defaultMessage: 'Privacy Information', id: 'global.account.link.privacy_information'},
    wishlist: {defaultMessage: 'Favorites', id: 'global.account.link.favorites'},
    repair: {defaultMessage: 'Repair service', id: 'global.account.link.repair_service'}
})

export const navLinks = [
    {
        name: 'profile',
        path: '',
        icon: UserIcon
    },
    {
        name: 'addresses',
        path: '/addresses',
        icon: BookIcon
    },
    {
        name: 'orders',
        path: '/orders',
        icon: CubeIcon
    },
    {
        name: 'privacy',
        path: '/privacy',
        icon: PrivacyIcon
    },
    {
        name: 'wishlist',
        path: '/wishlist',
        icon: WishlistIcon
    },
    {
        name: 'repair',
        path: '/repair',
        icon: RepairIcon
    }
]

export const CONFIRMATION_DIALOG_DEFAULT_CONFIG = defineMessages({
    dialogTitle: {
        defaultMessage: 'Confirm Action',
        id: 'confirmation_modal.default.title.confirm_action'
    },
    confirmationMessage: {
        defaultMessage: 'Are you sure you want to continue?',
        id: 'confirmation_modal.default.message.you_want_to_continue'
    },
    primaryActionLabel: {defaultMessage: 'Yes', id: 'confirmation_modal.default.action.yes'},
    primaryActionAriaLabel: {
        defaultMessage: 'Yes, confirm action',
        id: 'confirmation_modal.default.assistive_msg.yes'
    },
    alternateActionLabel: {defaultMessage: 'No', id: 'confirmation_modal.default.action.no'},
    alternateActionAriaLabel: {
        defaultMessage: 'No, cancel action',
        id: 'confirmation_modal.default.assistive_msg.no'
    }
})

export const COUNTRIES_WITH_STATES_LIST = ['US', 'CA', 'BR', 'MX', 'AU']

export const COUNTRIES_USING_POSTAL_CODE = [
    'DZ', // Algeria
    'AD', // Andorra
    'AM', // Armenia
    'AU', // Australia
    'AT', // Austria
    'AZ', // Azerbaijan
    'BD', // Bangladesh
    'BY', // Belarus
    'BE', // Belgium
    'BR', // Brazil
    'BN', // Brunei Darussalam
    'BG', // Bulgaria
    'CA', // Canada
    'IC', // Canary Islands
    'CN', // China
    'HR', // Croatia
    'CY', // Cyprus
    'CZ', // Czech Republic
    'DK', // Denmark
    'EE', // Estonia
    'FI', // Finland
    'FR', // France
    'GF', // French Guiana
    'GE', // Georgia
    'DE', // Germany
    'GR', // Greece
    'GL', // Greenland
    'GP', // Guadeloupe
    'GG', // Guernsey
    'VA', // Holy See (Vatican City State)
    'HU', // Hungary
    'IS', // Iceland
    'IN', // India
    'ID', // Indonesia
    'IL', // Israel
    'IT', // Italy
    'JP', // Japan
    'JE', // Jersey
    'KZ', // Kazakhstan
    'KG', // Kyrgyzstan
    'LV', // Latvia
    'LI', // Liechtenstein
    'LT', // Lithuania
    'LU', // Luxembourg
    'MK', // Macedonia
    'MG', // Madagascar
    'MY', // Malaysia
    'MV', // Maldives
    'MH', // Marshall Islands
    'MQ', // Martinique
    'MR', // Mauritania
    'YT', // Mayotte
    'MX', // Mexico
    'MD', // Moldova
    'MC', // Monaco
    'MN', // Mongolia
    'MA', // Morocco
    'NA', // Namibia
    'NL', // Netherlands
    'NC', // New Caledonia
    'NZ', // New Zealand
    'NO', // Norway
    'PK', // Pakistan
    'PW', // Palau
    'PG', // Papua New Guinea
    'PH', // Philippines
    'PL', // Poland
    'PT', // Portugal
    'RO', // Romania
    'RU', // Russian Federation
    'RE', // Réunion
    'PM', // Saint Pierre and Miquelon
    'RS', // Serbia
    'SG', // Singapore
    'SK', // Slovakia
    'SI', // Slovenia
    'ZA', // South Africa
    'KR', // South Korea
    'ES', // Spain
    'SZ', // Swaziland
    'SE', // Sweden
    'CH', // Switzerland
    'TW', // Taiwan
    'TJ', // Tajikistan
    'TH', // Thailand
    'TN', // Tunisia
    'TR', // Turkey
    'UA', // Ukraine
    'GB', // United Kingdom
    'US', // United States
    'UZ' // Uzbekistan
]

//regular expression for postal codes by country
//used by "checkZipCode" method and by the front-end validation
const postalCode4Digits = /^\d{4}$/
const postalCode5Digits = /^\d{5}$/
const postalCodeGB = /^[A-Z]{1,2}[0-9R][0-9A-Z]? [0-9][ABD-HJLNP-UW-Z]{2}$/
export const POSTAL_CODE_REGULAR_EXPRESSIONS = {
    'US': /^\d{5}(-\d{4})?$/,
    'CA': /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ] ?\d[ABCEGHJKLMNPRSTVWXYZ]\d$/,
    'FR': postalCode5Digits,
    'WF': postalCode5Digits,
    'GF': postalCode5Digits,
    'PF': postalCode5Digits,
    'GP': postalCode5Digits,
    'MQ': postalCode5Digits,
    'NC': postalCode5Digits,
    'MC': postalCode5Digits,
    'RE': postalCode5Digits,
    'MF': postalCode5Digits,
    'BL': postalCode5Digits,
    'PM': postalCode5Digits,
    'IT': postalCode5Digits,
    'SM': postalCode5Digits,
    'VA': postalCode5Digits,
    'GB': postalCodeGB,
    'UK': postalCodeGB,
    'JE': postalCodeGB,
    'GG': postalCodeGB,
    'IM': postalCodeGB,
    'DE': postalCode5Digits,
    'DK': /^(FO\d{3}|\d{3,4})$/,
    'FO': /^(FO\d{3}|\d{3,4})$/,
    'GL': /^(FO\d{3}|\d{3,4})$/,
    'ES': postalCode5Digits,
    'EA': postalCode5Digits,
    'IC': postalCode5Digits,
    'FI': postalCode5Digits,
    'AX': postalCode5Digits,
    'GR': /^\d{3}(\s)\d{2}$/,
    'NL': /^\d{4}(\s)[A-Z]{2}$/,
    'SE': /^\d{3}(\s)\d{2}$/,
    'AT': postalCode4Digits,
    'BE': postalCode4Digits,
    'IE': /^[A-Z]{2}(([A-Z])|(\s\d{2})|(\s\d)|(\s[A-Z]))$/,
    'LU': postalCode4Digits,
    'PT': /^\d{4}(-)\d{3}$/,
    'HU': postalCode4Digits,
    'HR': postalCode5Digits,
    'SK': /^\d{3}(\s)\d{2}$/,
    'SI': postalCode4Digits,
    'CZ': /^\d{3}(\s)\d{2}$/,
    'PL': /^\d{2}(-)\d{3}$/,
    'BG': postalCode4Digits,
    'LV': postalCode4Digits,
    'LT': postalCode5Digits,
    'EE': postalCode5Digits,
    'CY': postalCode4Digits,
    'RO': /^\d{6}$/,
    'MX': postalCode5Digits
};
